import { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { XMarkIcon } from '@heroicons/react/20/solid';
import {
	CalendarIcon,
	ClockIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import AppContext from '../../context/AppContext';

import { Field, FormRow, Label } from '../../components/Form/tailwindForm';
import Slider from '../../components/Shell/Slider';
import Button from '../../components/Button';
import { AssetGroupSelect } from '../../components/ui/asset-group-select';
import { TimePicker } from '../../components/ui/time-picker';
import { DurationPicker } from '../../components/ui/duration-picker';
import { DateRangePicker } from '../../components/ui/date-picker';
import { getUserFullName } from '../../components/helpers/functions';

import {
	createReservation,
	updateReservation,
	getReservation,
	verifyAvailability,
	getReservationCategories,
	createReservationCategory,
} from '../../api/reservations';
import { getReservableAssets, getAssetGroups } from '../../api/assets';
import { getTeam } from '../../api/company';
import { getAllPowerUsers } from '../../api/power-users';

const MODAL_NAME = 'addBooking';

// Default duration in minutes
const DEFAULT_DURATION = 60;

const defaultForm = {
	startDate: moment().format('YYYY-MM-DD'),
	endDate: moment().format('YYYY-MM-DD'),
	startTime: '9:0',
	endTime: '17:0', // Default end time for multi-day bookings
	duration: DEFAULT_DURATION, // Duration in minutes (used for same-day bookings)
	allDay: false, // Whether this is a full day booking
	assetIds: [],
	userIds: [],
	powerUserIds: [], // Power users (notification-only users without platform access)
	groupId: null, // If booking a group, this will be set
	categoryId: null, // Category for the reservation (booked, service, custom)
};

/**
 * Check if two dates are the same day
 */
function isSameDay(date1, date2) {
	return date1 === date2;
}

/**
 * Calculate end time from start time and duration (for same-day bookings)
 */
function calculateEndTimeFromDuration(startTime, durationMinutes) {
	if (!startTime) return null;
	const [h, m] = startTime.split(':').map(Number);
	const startMinutes = h * 60 + m;
	const endMinutes = startMinutes + durationMinutes;

	const endHour = Math.floor(endMinutes / 60) % 24;
	const endMinute = endMinutes % 60;

	return `${endHour}:${endMinute}`;
}

/**
 * Calculate duration from start and end times (for same-day bookings)
 */
function calculateDurationFromTimes(startTime, endTime) {
	if (!startTime || !endTime) return DEFAULT_DURATION;

	const [sh, sm] = startTime.split(':').map(Number);
	const [eh, em] = endTime.split(':').map(Number);

	const startMinutes = sh * 60 + sm;
	let endMinutes = eh * 60 + em;

	// Handle overnight bookings
	if (endMinutes <= startMinutes) {
		endMinutes += 24 * 60;
	}

	return endMinutes - startMinutes;
}

/**
 * Format time for display (12-hour format)
 */
function formatTimeDisplay(timeString) {
	if (!timeString) return '';
	const [h, m] = timeString.split(':').map(Number);
	const hour12 = h % 12 || 12;
	const ampm = h < 12 ? 'AM' : 'PM';
	const minutes = m.toString().padStart(2, '0');
	return `${hour12}:${minutes} ${ampm}`;
}

/**
 * Format date for display
 */
function formatDateDisplay(dateString) {
	return moment(dateString).format('ddd, MMM D, YYYY');
}

export default () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const appContext = useContext(AppContext);
	const currentUser = appContext?.user;

	const [form, setForm] = useState(defaultForm);
	const [isEditing, setIsEditing] = useState(false);
	const [editingReservationId, setEditingReservationId] = useState(null);
	const [availabilityCheck, setAvailabilityCheck] = useState(null);
	const [submitError, setSubmitError] = useState(null);
	const [newCategoryName, setNewCategoryName] = useState('');
	const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

	// Derived state
	const isSameDayBooking = isSameDay(form.startDate, form.endDate);

	// API hooks
	const { execute: onCreateReservation, status: createStatus } = useAsync(
		createReservation,
		{ immediate: false },
	);

	const { execute: onUpdateReservation, status: updateStatus } = useAsync(
		updateReservation,
		{ immediate: false },
	);

	const { value: reservation, execute: onGetReservation } = useAsync(
		getReservation,
		{ immediate: false },
	);

	const { execute: onVerifyAvailability, error: verificationError } = useAsync(
		verifyAvailability,
		{ immediate: false },
	);

	const { value: assets, execute: refreshAssets } = useAsync(
		getReservableAssets,
		{
			immediate: false,
		},
	);

	const { value: assetGroups, execute: refreshAssetGroups } = useAsync(
		getAssetGroups,
		{
			immediate: false,
		},
	);

	const { value: team, execute: refreshTeam } = useAsync(getTeam, {
		immediate: false,
	});

	const { value: powerUsers, execute: refreshPowerUsers } = useAsync(
		getAllPowerUsers,
		{
			immediate: false,
		},
	);

	const { value: categories, execute: refreshCategories } = useAsync(
		getReservationCategories,
		{
			immediate: false,
		},
	);

	const { execute: onCreateCategory, status: createCategoryStatus } = useAsync(
		createReservationCategory,
		{ immediate: false },
	);

	// Set default category when categories are loaded (for new bookings only)
	useEffect(() => {
		if (categories && categories.length > 0 && !isEditing && !form.categoryId) {
			// Find the first default category, or fall back to the first category
			const defaultCategory =
				categories.find((c) => c.isDefault) || categories[0];
			if (defaultCategory) {
				setForm((prev) => ({ ...prev, categoryId: defaultCategory.id }));
			}
		}
	}, [categories, isEditing]);

	// Reset form when modal opens/closes
	useEffect(() => {
		if (open) {
			// Capture editing state at the moment modal opens
			const editing = !!open?.id && open?.edit;
			setIsEditing(editing);
			setEditingReservationId(editing ? open.id : null);

			refreshAssets();
			refreshAssetGroups();
			refreshTeam();
			refreshPowerUsers();
			refreshCategories();
			setSubmitError(null);
			setNewCategoryName('');
			setShowNewCategoryInput(false);

			if (editing) {
				onGetReservation({ id: open.id });
			} else if (open.prefill) {
				// Pre-fill form with data from empty slot click
				const { assetIds, groupId, startMinutes, endMinutes, date } =
					open.prefill;
				const startHour = Math.floor(startMinutes / 60);
				const startMin = startMinutes % 60;
				const duration = endMinutes - startMinutes;
				const dateStr = moment(date).format('YYYY-MM-DD');

				setForm({
					...defaultForm,
					startDate: dateStr,
					endDate: dateStr,
					startTime: `${startHour}:${startMin}`,
					duration: duration,
					assetIds: assetIds || [],
					groupId: groupId || null,
					// Auto-select current user for new bookings
					userIds: currentUser?.id ? [currentUser.id] : [],
				});
				setAvailabilityCheck(null);
			} else {
				// Auto-select current user for new bookings
				setForm({
					...defaultForm,
					userIds: currentUser?.id ? [currentUser.id] : [],
				});
				setAvailabilityCheck(null);
			}
		} else {
			setForm(defaultForm);
			setAvailabilityCheck(null);
			setSubmitError(null);
			setIsEditing(false);
			setEditingReservationId(null);
		}
	}, [open, currentUser?.id]);

	// Populate form when editing
	useEffect(() => {
		if (reservation && isEditing) {
			const startMoment = moment(reservation.startTime);
			const endMoment = moment(reservation.endTime);
			const startDateStr = startMoment.format('YYYY-MM-DD');
			const endDateStr = endMoment.format('YYYY-MM-DD');
			const startTimeStr = `${startMoment.hour()}:${startMoment.minute()}`;
			const endTimeStr = `${endMoment.hour()}:${endMoment.minute()}`;

			// Check if it's a same-day booking
			const sameDay = startDateStr === endDateStr;

			// Check if it's an all-day booking (starts at 0:0 and ends at 23:59)
			const isAllDay =
				startMoment.hour() === 0 &&
				startMoment.minute() === 0 &&
				endMoment.hour() === 23 &&
				endMoment.minute() === 59;

			setForm({
				startDate: startDateStr,
				endDate: endDateStr,
				startTime: startTimeStr,
				endTime: endTimeStr,
				duration: sameDay
					? calculateDurationFromTimes(startTimeStr, endTimeStr)
					: DEFAULT_DURATION,
				allDay: isAllDay,
				assetIds: reservation.assets?.map((a) => a.id) || [],
				userIds: reservation.users?.map((u) => u.id) || [],
				powerUserIds: reservation.powerUsers?.map((pu) => pu.id) || [],
				groupId: reservation.assetGroupId || null,
				categoryId: reservation.categoryId || null,
			});
		}
	}, [reservation, isEditing]);

	// Auto-adjust end date if it's before start date
	useEffect(() => {
		if (form.endDate < form.startDate) {
			setForm((prev) => ({ ...prev, endDate: prev.startDate }));
		}
	}, [form.startDate, form.endDate]);

	// Check availability when assets or time changes
	useEffect(() => {
		setSubmitError(null);

		if (form.assetIds.length > 0 && form.startDate && form.startTime) {
			const debounce = setTimeout(() => {
				checkAvailability();
			}, 500);
			return () => clearTimeout(debounce);
		} else {
			setAvailabilityCheck(null);
		}
	}, [
		form.assetIds,
		form.startDate,
		form.endDate,
		form.startTime,
		form.endTime,
		form.duration,
		form.allDay,
	]);

	// Build start and end ISO times from form state
	const buildTimeRange = () => {
		const [sh, sm] = form.startTime.split(':').map(Number);
		let startTime, endTime;

		if (form.allDay) {
			// All day: start at beginning of start date, end at end of end date
			startTime = moment(form.startDate).startOf('day').toISOString();
			endTime = moment(form.endDate).endOf('day').toISOString();
		} else if (isSameDayBooking) {
			// Same day: use start time + duration
			const calculatedEndTime = calculateEndTimeFromDuration(
				form.startTime,
				form.duration,
			);
			const [eh, em] = calculatedEndTime.split(':').map(Number);
			startTime = moment(form.startDate)
				.hour(sh)
				.minute(sm)
				.second(0)
				.toISOString();
			endTime = moment(form.startDate)
				.hour(eh)
				.minute(em)
				.second(0)
				.toISOString();
		} else {
			// Multi-day: use start time on start date, end time on end date
			const [eh, em] = form.endTime.split(':').map(Number);
			startTime = moment(form.startDate)
				.hour(sh)
				.minute(sm)
				.second(0)
				.toISOString();
			endTime = moment(form.endDate)
				.hour(eh)
				.minute(em)
				.second(0)
				.toISOString();
		}

		return { startTime, endTime };
	};

	const checkAvailability = async () => {
		const { startTime, endTime } = buildTimeRange();

		try {
			const result = await onVerifyAvailability({
				assetIds: form.assetIds,
				startTime,
				endTime,
				excludeReservationId: isEditing ? open.id : undefined,
			});
			setAvailabilityCheck(result);
		} catch (err) {
			console.error('Availability check failed:', err);
		}
	};

	const handleSubmit = async () => {
		setSubmitError(null);

		const { startTime, endTime } = buildTimeRange();

		// Verify availability before submitting
		try {
			await onVerifyAvailability({
				assetIds: form.assetIds,
				startTime,
				endTime,
				excludeReservationId: isEditing ? editingReservationId : undefined,
			});

			if (verificationError) {
				setSubmitError({
					type: 'verification',
					message: verificationError?.message,
				});
				return;
			}
		} catch (err) {
			setSubmitError({
				type: 'verification',
				message:
					typeof err === 'string'
						? err
						: err?.message ||
							'Failed to verify availability. Please try again.',
			});
			return;
		}

		// Proceed with creating/updating the reservation
		const payload = {
			startTime,
			endTime,
			assetIds: form.assetIds,
			userIds: form.userIds,
			powerUserIds: form.powerUserIds,
			categoryId: form.categoryId,
		};

		// Include groupId for group reservations (only on create, not edit)
		if (form.groupId && !isEditing) {
			payload.groupId = form.groupId;
		}

		try {
			if (isEditing && editingReservationId) {
				await onUpdateReservation({ id: editingReservationId, ...payload });
			} else {
				await onCreateReservation(payload);
			}
			setClose();
		} catch (err) {
			setSubmitError({
				type: 'submit',
				message:
					typeof err === 'string'
						? err
						: err?.message || 'Failed to save booking. Please try again.',
			});
		}
	};

	const toggleUser = (userId) => {
		if (form.userIds.includes(userId)) {
			setForm({
				...form,
				userIds: form.userIds.filter((id) => id !== userId),
			});
		} else {
			setForm({
				...form,
				userIds: [...form.userIds, userId],
			});
		}
	};

	const togglePowerUser = (powerUserId) => {
		if (form.powerUserIds.includes(powerUserId)) {
			setForm({
				...form,
				powerUserIds: form.powerUserIds.filter((id) => id !== powerUserId),
			});
		} else {
			setForm({
				...form,
				powerUserIds: [...form.powerUserIds, powerUserId],
			});
		}
	};

	const handleAllDayToggle = () => {
		setForm((prev) => ({
			...prev,
			allDay: !prev.allDay,
			// Reset times when toggling all day
			startTime: !prev.allDay ? '0:0' : '9:0',
			endTime: !prev.allDay ? '23:59' : '17:0',
		}));
	};

	const handleCreateCategory = async () => {
		if (!newCategoryName.trim()) return;

		try {
			const newCategory = await onCreateCategory({
				name: newCategoryName.trim(),
			});
			await refreshCategories();
			setForm((prev) => ({ ...prev, categoryId: newCategory.id }));
			setNewCategoryName('');
			setShowNewCategoryInput(false);
		} catch (err) {
			console.error('Failed to create category:', err);
		}
	};

	// At least one user OR power user must be selected
	const hasAtLeastOneRecipient =
		form.userIds.length > 0 || form.powerUserIds.length > 0;

	const isValid =
		form.assetIds.length > 0 &&
		hasAtLeastOneRecipient &&
		form.startDate &&
		(form.allDay || form.startTime) &&
		availabilityCheck?.isAvailable !== false;

	const isSaving = createStatus === 'pending' || updateStatus === 'pending';

	const title = isEditing ? 'Edit Booking' : 'New Booking';

	// Get selected users details
	const selectedUsers = form.userIds
		.map((id) => team?.rows?.find((u) => u.id === id))
		.filter(Boolean);

	// Get selected power users details
	const selectedPowerUsers = form.powerUserIds
		.map((id) => powerUsers?.find((pu) => pu.id === id))
		.filter(Boolean);

	// Calculate display end time for same-day bookings
	const displayEndTime =
		isSameDayBooking && !form.allDay
			? formatTimeDisplay(
					calculateEndTimeFromDuration(form.startTime, form.duration),
				)
			: formatTimeDisplay(form.endTime);

	console.log('form: ', form);

	return (
		<Slider name={MODAL_NAME} title={title} form={true} wide>
			<div className="space-y-6">
				{/* Submit Error Alert */}
				{submitError && (
					<div className="rounded-md bg-red-50 p-4">
						<div className="flex">
							<div className="flex-shrink-0">
								<ExclamationTriangleIcon
									className="h-5 w-5 text-red-400"
									aria-hidden="true"
								/>
							</div>
							<div className="ml-3">
								<h3 className="text-sm font-medium text-red-800">
									{submitError.type === 'availability'
										? 'Booking Conflict'
										: submitError.type === 'verification'
											? 'Verification Failed'
											: 'Error Saving Booking'}
								</h3>
								<div className="mt-2 text-sm text-red-700">
									<p>{submitError.message}</p>
									{submitError.details?.conflictingReservations?.length > 0 && (
										<div className="mt-2">
											<p className="font-medium">Conflicting bookings:</p>
											<ul className="list-disc list-inside mt-1">
												{submitError.details.conflictingReservations.map(
													(conflict, i) => (
														<li key={i}>
															{moment(conflict.startTime).format(
																'MMM D, h:mm A',
															)}{' '}
															-{' '}
															{moment(conflict.endTime).format('MMM D, h:mm A')}
														</li>
													),
												)}
											</ul>
										</div>
									)}
									{submitError.details?.AvailableTimeSlots?.length > 0 && (
										<div className="mt-2">
											<p className="font-medium">
												Suggested available time slots:
											</p>
											<ul className="list-disc list-inside mt-1">
												{submitError.details.AvailableTimeSlots.slice(0, 3).map(
													(slot, i) => (
														<li key={i}>
															{moment(slot.start).format('MMM D, h:mm A')} -{' '}
															{moment(slot.end).format('h:mm A')}
														</li>
													),
												)}
											</ul>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Availability Warning (real-time check) */}
				{!submitError &&
					availabilityCheck &&
					!availabilityCheck.isAvailable && (
						<div className="rounded-md bg-yellow-50 p-4">
							<div className="flex">
								<div className="flex-shrink-0">
									<ExclamationTriangleIcon
										className="h-5 w-5 text-yellow-400"
										aria-hidden="true"
									/>
								</div>
								<div className="ml-3">
									<h3 className="text-sm font-medium text-yellow-800">
										Availability Conflict
									</h3>
									<div className="mt-2 text-sm text-yellow-700">
										<p>{availabilityCheck.message}</p>
										{availabilityCheck.AvailableTimeSlots?.length > 0 && (
											<div className="mt-2">
												<p className="font-medium">Suggested time slots:</p>
												<ul className="list-disc list-inside mt-1">
													{availabilityCheck.AvailableTimeSlots.slice(0, 3).map(
														(slot, i) => (
															<li key={i}>
																{moment(slot.start).format('MMM D, h:mm A')} -{' '}
																{moment(slot.end).format('h:mm A')}
															</li>
														),
													)}
												</ul>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					)}

				{/* Date Selection */}
				<div className="space-y-4">
					<FormRow>
						<Label>
							<CalendarIcon className="inline h-4 w-4 mr-1" />
							Booking Dates
						</Label>
						<Field>
							<DateRangePicker
								startDate={form.startDate}
								endDate={form.endDate}
								onStartDateChange={(startDate) =>
									setForm({ ...form, startDate })
								}
								onEndDateChange={(endDate) => setForm({ ...form, endDate })}
								minDate={moment().format('YYYY-MM-DD')}
							/>
						</Field>
					</FormRow>

					{/* All Day Toggle */}
					<div className="flex items-center">
						<input
							type="checkbox"
							id="allDay"
							checked={form.allDay}
							onChange={handleAllDayToggle}
							className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
						/>
						<label htmlFor="allDay" className="ml-2 text-sm text-gray-700">
							All day booking
						</label>
						<span className="ml-2 text-xs text-gray-500">
							(Reserves from start of first day to end of last day)
						</span>
					</div>

					{/* Time Selection - Only show if not all day */}
					{!form.allDay && (
						<div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
							{/* Start Time */}
							<div className="grid grid-cols-2 gap-4">
								<FormRow>
									<Label>
										<ClockIcon className="inline h-4 w-4 mr-1" />
										Start Time
									</Label>
									<Field>
										<TimePicker
											value={form.startTime}
											onChange={(startTime) => setForm({ ...form, startTime })}
											interval={15}
										/>
									</Field>
								</FormRow>

								{/* End Time - Only show for multi-day bookings */}
								{!isSameDayBooking && (
									<FormRow>
										<Label>
											<ClockIcon className="inline h-4 w-4 mr-1" />
											End Time (on {formatDateDisplay(form.endDate)})
										</Label>
										<Field>
											<TimePicker
												value={form.endTime}
												onChange={(endTime) => setForm({ ...form, endTime })}
												interval={15}
											/>
										</Field>
									</FormRow>
								)}
							</div>

							{/* Duration Picker - Only show for same-day bookings */}
							{isSameDayBooking && (
								<>
									<DurationPicker
										value={form.duration}
										onChange={(duration) => setForm({ ...form, duration })}
										min={15}
										max={1425} // Up to 23:45 (leaving 15 min before midnight)
										step={15}
									/>
									{/* End time display */}
									<div className="pt-3 border-t border-gray-200 flex items-center justify-between text-sm">
										<span className="text-gray-500">Booking ends at:</span>
										<span className="font-medium text-gray-900">
											{displayEndTime}
										</span>
									</div>
								</>
							)}

							{/* Multi-day summary */}
							{!isSameDayBooking && (
								<div className="pt-3 border-t border-gray-200">
									<div className="bg-indigo-50 rounded-md p-3 text-sm">
										<p className="font-medium text-indigo-900">
											Multi-day Booking
										</p>
										<p className="text-indigo-700 mt-1">
											Starts: {formatDateDisplay(form.startDate)} at{' '}
											{formatTimeDisplay(form.startTime)}
										</p>
										<p className="text-indigo-700">
											Ends: {formatDateDisplay(form.endDate)} at{' '}
											{formatTimeDisplay(form.endTime)}
										</p>
									</div>
								</div>
							)}
						</div>
					)}

					{/* All Day Summary */}
					{form.allDay && (
						<div className="bg-indigo-50 rounded-md p-3 text-sm">
							<p className="font-medium text-indigo-900">
								{isSameDayBooking
									? 'Full Day Booking'
									: 'Multi-day All Day Booking'}
							</p>
							<p className="text-indigo-700 mt-1">
								{isSameDayBooking
									? `Reserved for the entire day of ${formatDateDisplay(form.startDate)}`
									: `Reserved from ${formatDateDisplay(form.startDate)} through ${formatDateDisplay(form.endDate)} (all day)`}
							</p>
						</div>
					)}
				</div>

				<FormRow>
					<Label>Assets to Reserve</Label>
					<Field>
						<AssetGroupSelect
							assets={assets || []}
							assetGroups={assetGroups || []}
							selectedAssetIds={form.assetIds}
							selectedGroupId={form.groupId}
							onAssetIdsChange={(assetIds) => {
								console.log('asset ID change: ', assetIds);
								setForm((prev) => ({ ...prev, assetIds }));
							}}
							onGroupIdChange={(groupId) => {
								console.log('groupId change: ', groupId);
								setForm((prev) => ({ ...prev, groupId }));
							}}
							placeholder="Select a group or search for individual assets..."
						/>
					</Field>
				</FormRow>

				{/* Users Section */}
				<FormRow>
					<Label>Users</Label>
					<Field>
						<div className="border rounded-md p-3 max-h-48 overflow-y-auto">
							{team?.rows?.map((user) => (
								<label
									key={user.id}
									className="flex items-center py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
								>
									<input
										type="checkbox"
										checked={form.userIds.includes(user.id)}
										onChange={() => toggleUser(user.id)}
										className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
									/>
									<span className="ml-3 text-sm text-gray-900">
										{getUserFullName(user)}
									</span>
									{user.email && (
										<span className="ml-2 text-xs text-gray-500">
											({user.email})
										</span>
									)}
								</label>
							))}
							{(!team?.rows || team.rows.length === 0) && (
								<p className="text-sm text-gray-500 py-2">
									No team members found
								</p>
							)}
						</div>
					</Field>
				</FormRow>

				{/* Power Users Section */}
				<FormRow>
					<Label>Power Users</Label>
					<Field>
						<div className="border rounded-md p-3 max-h-48 overflow-y-auto">
							{powerUsers?.map((powerUser) => (
								<label
									key={powerUser.id}
									className="flex items-center py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
								>
									<input
										type="checkbox"
										checked={form.powerUserIds.includes(powerUser.id)}
										onChange={() => togglePowerUser(powerUser.id)}
										className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
									/>
									<span className="ml-3 text-sm text-gray-900">
										{powerUser.firstName} {powerUser.lastName}{' '}
										{!powerUser.firstName &&
											!powerUser.lastName &&
											'Not provided'}
									</span>
									{powerUser.email && (
										<span className="ml-2 text-xs text-gray-500">
											({powerUser.email})
										</span>
									)}
								</label>
							))}
							{(!powerUsers || powerUsers.length === 0) && (
								<p className="text-sm text-gray-500 py-2">
									No power users found
								</p>
							)}
						</div>
					</Field>
				</FormRow>

				{/* Category Section */}
				<FormRow>
					<Label>
						Category{' '}
						<span className="text-gray-400 font-light">(optional)</span>
					</Label>
					<Field>
						<div className="space-y-3">
							<select
								value={form.categoryId || ''}
								onChange={(e) =>
									setForm((prev) => ({
										...prev,
										categoryId: e.target.value || null,
									}))
								}
								className="block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
							>
								<option value="">Select a category...</option>
								{categories?.map((category) => (
									<option key={category.id} value={category.id}>
										{category.name}
										{category.isDefault ? ' (Default)' : ''}
									</option>
								))}
							</select>

							{/* Add New Category */}
							{!showNewCategoryInput ? (
								<button
									type="button"
									onClick={() => setShowNewCategoryInput(true)}
									className="text-sm text-indigo-600 hover:text-indigo-500"
								>
									+ Add custom category
								</button>
							) : (
								<div className="flex items-center gap-2">
									<input
										type="text"
										value={newCategoryName}
										onChange={(e) => setNewCategoryName(e.target.value)}
										placeholder="Enter category name"
										className="block flex-1 rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
									/>
									<button
										type="button"
										onClick={handleCreateCategory}
										disabled={
											!newCategoryName.trim() ||
											createCategoryStatus === 'pending'
										}
										className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{createCategoryStatus === 'pending' ? 'Adding...' : 'Add'}
									</button>
									<button
										type="button"
										onClick={() => {
											setShowNewCategoryInput(false);
											setNewCategoryName('');
										}}
										className="text-sm text-gray-500 hover:text-gray-700"
									>
										Cancel
									</button>
								</div>
							)}
						</div>
					</Field>
				</FormRow>

				{/* Submit Button */}
				<FormRow alignRight className="pt-4 ">
					<Button disabled={!isValid || isSaving} onClick={handleSubmit}>
						{isSaving
							? 'Saving...'
							: isEditing
								? 'Update Booking'
								: 'Create Booking'}
					</Button>
				</FormRow>
			</div>
		</Slider>
	);
};
